export type MinimalChannel = {
  _id: string;
};

export type ResolveBestChannelParams<T extends MinimalChannel> = {
  channels: T[];
  defaultChannelIdFromUser?: string | null;
  isHomeChannelSelectionEnabled: boolean;
  fallbackToFirstChannel: boolean;
  getStoredValue: () => Promise<unknown>;
  onInvalidHomeChannel: () => void;
  isBuildingChannel: (channel: T) => boolean;
  isTenantLocationChannel: (channel: T) => boolean;
  isSubTenantLocationChannel: (channel: T) => boolean;
  sortByDistance: (channels: T[]) => T[];
};

function findNearestByType<T extends MinimalChannel>(
  channels: T[],
  isOfType: (channel: T) => boolean,
  sortByDistance: (channels: T[]) => T[]
): T | undefined {
  return sortByDistance(channels.filter(isOfType))[0];
}

export async function resolveBestChannel<T extends MinimalChannel>({
  channels,
  defaultChannelIdFromUser,
  isHomeChannelSelectionEnabled,
  fallbackToFirstChannel,
  getStoredValue,
  onInvalidHomeChannel,
  isBuildingChannel,
  isTenantLocationChannel,
  isSubTenantLocationChannel,
  sortByDistance,
}: ResolveBestChannelParams<T>): Promise<T | null> {
  if (isHomeChannelSelectionEnabled && defaultChannelIdFromUser) {
    const homeChannel = channels.find(
      channel => channel._id === defaultChannelIdFromUser
    );

    if (homeChannel) {
      return homeChannel;
    }

    onInvalidHomeChannel();
  }

  const storedValue = await getStoredValue().catch(() => null);
  const storedChannel = channels.find(channel => channel._id === storedValue);

  if (storedChannel) {
    return storedChannel;
  }

  const nearestByType =
    findNearestByType(channels, isBuildingChannel, sortByDistance) ||
    findNearestByType(channels, isTenantLocationChannel, sortByDistance) ||
    findNearestByType(channels, isSubTenantLocationChannel, sortByDistance);

  if (nearestByType) {
    return nearestByType;
  }

  if (fallbackToFirstChannel && channels.length > 0) {
    return channels[0];
  }

  return null;
}
